export const CONSENT_FIELD_GROUPS = [
  {
    key: "patient",
    label: "Patient",
    fields: [
      { key: "patient.name", label: "Patient name", sample: "Rahul Sharma" },
      { key: "patient.age", label: "Age", sample: "34" },
      { key: "patient.gender", label: "Gender", sample: "Male" },
      { key: "patient.uhid", label: "UHID", sample: "DRC-000124" },
      { key: "patient.mobile", label: "Mobile", sample: "98765 43210" },
      { key: "patient.address", label: "Address", sample: "12, MG Road, Pune" },
      {
        key: "patient.guardianName",
        label: "Guardian name",
        sample: "Sunita Sharma",
      },
    ],
  },
  {
    key: "appointment",
    label: "Appointment",
    fields: [
      { key: "appointment.date", label: "Appointment date", sample: "06-10-2026" },
      { key: "appointment.time", label: "Appointment time", sample: "11:30 AM" },
    ],
  },
  {
    key: "doctor",
    label: "Doctor",
    fields: [
      { key: "doctor.name", label: "Doctor name", sample: "Dr. A. Mehta" },
      {
        key: "doctor.registrationNo",
        label: "Registration no.",
        sample: "MH-2011-45231",
      },
      { key: "doctor.speciality", label: "Speciality", sample: "Dentist" },
      // The rest of the letterhead; each resolves from the Doctor record.
      { key: "doctor.qualification", label: "Qualification", sample: "BDS, MDS" },
      { key: "doctor.designation", label: "Designation", sample: "Consultant" },
      { key: "doctor.department", label: "Department", sample: "Dental" },
      { key: "doctor.experience", label: "Experience", sample: "12 years" },
    ],
  },
  {
    key: "clinical",
    label: "Clinical",
    fields: [
      { key: "diagnosis", label: "Diagnosis", sample: "Irreversible pulpitis" },
      { key: "treatment", label: "Treatment", sample: "Root canal treatment" },
      { key: "procedure", label: "Procedure", sample: "RCT — single sitting" },
      { key: "tooth.number", label: "Tooth number (FDI)", sample: "36" },
    ],
  },
  {
    key: "cost",
    label: "Cost",
    fields: [
      { key: "estimatedCost", label: "Estimated cost", sample: "₹ 7,500" },
    ],
  },
  {
    key: "clinic",
    label: "Clinic",
    fields: [
      { key: "clinic.name", label: "Clinic name", sample: "DRCFO Dental Care" },
      {
        key: "clinic.address",
        label: "Clinic address",
        sample: "2nd Floor, Shree Complex, Pune 411001",
      },
      { key: "clinic.phone", label: "Clinic phone", sample: "020 2345 6789" },
      { key: "clinic.email", label: "Clinic email", sample: "care@drcfodental.in" },
      // Derived from the clinic's own appointment availabilities.
      {
        key: "clinic.timings",
        label: "Clinic timings",
        sample: "Mon, Tue, Wed, Thu, Fri 10:00 – 19:00",
      },
    ],
  },
  {
    key: "consent",
    label: "Consent",
    fields: [
      { key: "consent.date", label: "Consent date", sample: "06-10-2026" },
      { key: "consent.place", label: "Place", sample: "Pune" },
    ],
  },
];

export const CONSENT_FIELDS = CONSENT_FIELD_GROUPS.flatMap(
  (group) => group.fields
);

const FIELD_BY_KEY = new Map(CONSENT_FIELDS.map((field) => [field.key, field]));

export const consentField = (key) => FIELD_BY_KEY.get(key) || null;

export const fieldLabel = (key) => consentField(key)?.label || key || "";

export const placeholderOf = (key) => (key ? `{{${key}}}` : "");

/** Sample values the Preview renders with, so a design can be read as a form. */
export const SAMPLE_CONTEXT = CONSENT_FIELDS.reduce((values, field) => {
  values[field.key] = field.sample;
  return values;
}, {});

export const CLINIC_NAME_FIELD = "clinic.name";
export const DOCTOR_NAME_FIELD = "doctor.name";

export const consentHeaderLayout = (fields) => {
  const list = (Array.isArray(fields) ? fields : []).filter(Boolean);

  return {
    clinicName: list.includes(CLINIC_NAME_FIELD) ? CLINIC_NAME_FIELD : "",
    doctorName: list.includes(DOCTOR_NAME_FIELD) ? DOCTOR_NAME_FIELD : "",
    clinicLines: list.filter(
      (field) => field !== CLINIC_NAME_FIELD && !field.startsWith("doctor.")
    ),
    doctorLines: list.filter(
      (field) => field !== DOCTOR_NAME_FIELD && field.startsWith("doctor.")
    ),
  };
};

const LABELLED_HEADER_FIELDS = new Set([
  "clinic.address",
  "clinic.phone",
  "clinic.email",
  "clinic.timings",
  "doctor.registrationNo",
]);

export const headerFieldIsLabelled = (field) =>
  LABELLED_HEADER_FIELDS.has(field);

const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;

export const extractPlaceholders = (text) => {
  if (typeof text !== "string") return [];

  const found = [];
  for (const match of text.matchAll(PLACEHOLDER)) {
    if (!found.includes(match[1])) found.push(match[1]);
  }
  return found;
};

/** Placeholders the registry does not know — flagged, never silently printed. */
export const unknownPlaceholders = (text) =>
  extractPlaceholders(text).filter((key) => !FIELD_BY_KEY.has(key));

export const resolvePlaceholders = (text, values = SAMPLE_CONTEXT, fallback) => {
  if (typeof text !== "string") return "";

  return text.replace(PLACEHOLDER, (whole, key) => {
    const value = values?.[key];
    if (value !== undefined && value !== null && String(value) !== "") {
      return String(value);
    }
    return fallback !== undefined ? fallback : whole;
  });
};
